import {
  IonButton,
  IonIcon,
  IonLabel,
  IonSpinner,
  isPlatform,
} from '@ionic/react';
import { documentOutline, openOutline, refreshOutline } from 'ionicons/icons';
import {
  MouseEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BsFileEarmarkPdf } from 'react-icons/bs';
import { Attachment } from '../../api/graphql/generated';
import {
  ImageResolution,
  useAttachmentUrlLazy,
} from '../../api/hooks/attachment.hooks';
import { isImage, isPDF } from '../../util/file-utils';
import { openLink } from '../../util/link-utils';
import './attachment-preview.css';

interface AttachmentPreviewProps {
  attachment: Attachment;
  resolution?: ImageResolution;
  isSmallPreview?: boolean;
}

export const AttachmentPreview = (
  props: AttachmentPreviewProps
): ReactElement => {
  const { attachment, resolution, isSmallPreview } = props;
  const { i18n } = useTranslation();

  const attachmentIsImage = isImage(attachment.contentType);
  const attachmentIsPDF = isPDF(attachment.contentType);
  const [isLoaded, setLoaded] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const getAttachmentUrl = useAttachmentUrlLazy();

  const src = getAttachmentUrl(attachment.path, attachment.contentType, {
    resolution,
  });

  useEffect(() => {
    if (!attachmentIsImage) {
      setLoaded(true);
    }
  }, [attachmentIsImage]);

  const showPreview =
    (attachmentIsImage && src !== undefined) || attachmentIsPDF;

  const [tries, setTries] = useState(1);
  const reloadImage: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setTries(tries + 1);
      setHasError(false);
      setLoaded(false);
    },
    [tries]
  );

  let content = (
    <img
      key={`${attachment.name}-${tries}`}
      src={src}
      alt={attachment.name}
      title={attachment.name}
      onLoad={() => setLoaded(true)}
      onError={() => {
        setHasError(true);
        setLoaded(true);
      }}
    />
  );
  if (attachmentIsPDF) {
    const isDesktop = isPlatform('desktop');
    if (isDesktop) {
      content = (
        <object
          data={`${src}#toolbar=0&navpanes=0`}
          type={attachment.contentType}
          height={'100%'}
          width={'100%'}
        >
          <IonIcon
            icon={documentOutline}
            size="large"
            color="primary"
            title={attachment.name}
          />
        </object>
      );
    } else {
      if (isSmallPreview) {
        content = (
          <IonLabel color={'primary'} className="pdf-small-preview-container">
            <BsFileEarmarkPdf />
          </IonLabel>
        );
      } else {
        content = (
          <IonLabel
            color={'primary'}
            className="pdf-preview-container"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (src) {
                openLink(src);
              }
            }}
          >
            {i18n.t('common:attachments_no_preview_available')}
            <p>
              {i18n.t('common:attachments_open_in_external_window')}
              <IonIcon icon={openOutline} />
            </p>
          </IonLabel>
        );
      }
    }
  }

  return (
    <>
      {hasError ? (
        <RetryButton onClick={reloadImage} />
      ) : showPreview ? (
        content
      ) : (
        <IonIcon
          icon={documentOutline}
          size="large"
          color="primary"
          title={attachment.name}
        />
      )}
      {!isLoaded && <IonSpinner className="attachment-preview-spinner" />}
    </>
  );
};

interface RetryButtonProps {
  onClick: MouseEventHandler;
}

const RetryButton = (props: RetryButtonProps): ReactElement => {
  const { onClick } = props;
  return (
    <IonButton
      onClick={onClick}
      fill="clear"
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <IonIcon icon={refreshOutline} slot="icon-only" />
    </IonButton>
  );
};
